import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isYdelserOpen, setIsYdelserOpen] = useState(false);
  const location = useLocation();
  
  const isHomePage = location.pathname === '/';
  const isDarkHero = isHomePage && !isScrolled;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsYdelserOpen(false);
  }, [location]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const ydelserItems = [
    { href: '/ydelser/energitjek', label: 'Energitjek' },
    { href: '/ydelser/energioptimering', label: 'Energioptimering' },
    { href: '/ydelser/lysstyring', label: 'Intelligent lysstyring' },
    { href: '/ydelser/service', label: 'Service & vedligehold' },
    { href: '/nybyg-og-renovering', label: 'Nybyg & renovering' },
  ];

  const navLinks = [
    // { href: '/cases', label: 'Cases' }, // Skjult indtil kunden har flere cases
    { href: '/om-os', label: 'Om os' },
    { href: '/esg', label: 'ESG' },
    { href: '/ledige-stillinger', label: 'Ledige stillinger' },
    { href: '/kontakt', label: 'Kontakt' },
  ];

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled ? 'bg-[#F4F2EE]/90 backdrop-blur-md' : 'bg-transparent'
      }`}
    >
      <nav className="max-w-[1800px] mx-auto px-8 md:px-12 py-6 flex items-center justify-between">
        {/* Logo */}
        <Link
          to="/"
          className="relative z-10"
          data-testid="header-logo"
        >
          <img loading="lazy" 
            src="/images/dilling_el_logo.svg" 
            alt="Dilling El" 
            className={`h-10 md:h-12 lg:h-14 w-auto transition-all duration-500 ${
              isDarkHero ? 'brightness-0 invert' : ''
            }`}
          />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-10">
          {/* Ydelser Dropdown */}
          <div 
            className="relative"
            onMouseEnter={() => setIsYdelserOpen(true)}
            onMouseLeave={() => setIsYdelserOpen(false)}
          >
            <button
              className={`flex items-center gap-1 text-[13px] tracking-[0.1em] uppercase transition-colors duration-300 py-2 ${
                isDarkHero 
                  ? 'text-white/80 hover:text-white' 
                  : 'text-[#1E2A24]/60 hover:text-[#1E2A24]'
              }`}
              data-testid="nav-ydelser-dropdown"
            >
              Ydelser
              <ChevronDown size={14} className={`transition-transform ${isYdelserOpen ? 'rotate-180' : ''}`} />
            </button>
            
            <AnimatePresence>
              {isYdelserOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.2 }}
                  className="absolute top-full left-0 pt-2"
                >
                  <div className="bg-[#F4F2EE] shadow-lg border border-[#A6B8A2] min-w-[220px]">
                    {ydelserItems.map((item) => (
                      <Link
                        key={item.href}
                        to={item.href}
                        className="block px-6 py-3 text-sm text-[#1E2A24]/70 hover:text-[#0E3D24] hover:bg-white/50 transition-colors"
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={`text-[13px] tracking-[0.1em] uppercase transition-colors duration-300 link-underline ${
                isDarkHero 
                  ? 'text-white/80 hover:text-white' 
                  : 'text-[#1E2A24]/60 hover:text-[#1E2A24]'
              } ${location.pathname === link.href ? (isDarkHero ? 'text-white' : 'text-[#1E2A24]') : ''}`}
              data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        {/* Mobile Menu Button */}
        <button
          className={`lg:hidden p-2 transition-colors duration-300 ${
            isDarkHero ? 'text-white' : 'text-[#1E2A24]'
          }`}
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          data-testid="mobile-menu-toggle"
        >
          {isMobileMenuOpen ? <X size={24} strokeWidth={1} /> : <Menu size={24} strokeWidth={1} />}
        </button>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-[#F4F2EE] z-[60] lg:hidden overflow-hidden"
            data-testid="mobile-menu"
          >
            {/* Close button in top right */}
            <button
              className="absolute top-6 right-8 text-[#1E2A24] z-10"
              onClick={() => setIsMobileMenuOpen(false)}
              data-testid="mobile-menu-close"
            >
              <X size={24} strokeWidth={1} />
            </button>
            
            <div className="flex flex-col items-center justify-center h-full gap-6 pt-20 pb-10 overflow-y-auto">
              <Link
                to="/"
                className="text-[13px] tracking-[0.1em] uppercase text-[#1E2A24]/60 hover:text-[#1E2A24] transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Forside
              </Link>
              
              {/* Mobile Ydelser */}
              <div className="flex flex-col items-center gap-3">
                <span className="text-[13px] tracking-[0.1em] uppercase text-[#1E2A24]">Ydelser</span>
                {ydelserItems.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className="text-[12px] tracking-[0.1em] uppercase text-[#1E2A24]/50 hover:text-[#1E2A24] transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>

              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`text-[13px] tracking-[0.1em] uppercase transition-colors ${
                    location.pathname === link.href 
                      ? 'text-[#1E2A24]' 
                      : 'text-[#1E2A24]/60 hover:text-[#1E2A24]'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
